import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BrowserFrame } from "@/marketing/DeviceFrames";

const SHOTS = {
  dashboard: "/mockups/dashboard.png",
  quote: "/mockups/quotes.png",
  alfie: "/mockups/alfie.png",
  website: "/mockups/sample-website.jpg",
  crm: "/mockups/dashboard.png",
  team: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const CONTENT = {
  features: {
    title: "Everything in one place",
    intro: "Amden One replaces the pile of apps, spreadsheets and paper you use to run your business.",
    hero: SHOTS.crm,
    gallery: [[SHOTS.dashboard, "Dashboard"], [SHOTS.quote, "Quotes & estimates"], [SHOTS.alfie, "Alfie AI assistant"]],
    sections: [
      ["Customer Hub", "Keep every customer, contact and note together with a complete activity timeline of quotes, invoices and jobs."],
      ["Leads Pipeline", "Track enquiries on a drag-and-drop board with your own custom stages, or switch to a list view."],
      ["Quotes & Invoices", "VAT-aware line items, professional branded PDFs, status tracking, payment recording and aged-debtor reports."],
      ["Jobs, Planner & Sign-off", "Schedule work, assign your team, capture on-site reports, photos and customer signatures on mobile."],
      ["Expenses & VAT", "Log costs, snap receipts and get clear VAT summaries — in plain language, not accountant-speak."],
      ["Team, Time & Materials", "Invite employees, run a time clock, approve timesheets, and track materials with job margins."],
    ],
  },
  alfie: {
    title: "Alfie AI — paperwork, handled",
    intro: "Alfie is your built-in assistant. It drafts, summarises and suggests — but never sends or changes anything without your approval.",
    hero: SHOTS.alfie,
    sections: [
      ["Draft, don't do", "Alfie proposes a draft quote, invoice reminder or summary. You review, edit and approve before anything happens."],
      ["Context-aware", "Alfie knows your open quotes, unpaid invoices and customers, so its drafts are relevant and accurate."],
      ["Approval workflow", "Every AI action is logged. You approve or reject each one — no autonomous sends, no destructive actions."],
      ["Plain and polite", "Reminders and messages come out courteous and professional, in clear everyday English."],
    ],
  },
  website: {
    title: "Website Add-on",
    intro: "Let Amden build and manage a simple professional website for your business — and turn visitors into leads automatically.",
    hero: SHOTS.website,
    heroUrl: "www.yourbusiness.ie",
    sections: [
      ["Managed for you", "Submit your details, service areas, logo and social links. Our team builds and hosts the site."],
      ["Enquiry to lead", "Every website enquiry drops straight into your Leads pipeline tagged 'Website' — no copy-pasting."],
      ["Change requests", "Need an update? Raise a request from inside your workspace and track its status."],
      ["Basic & Full plans", "The website add-on is an extra €15/month, available on the Basic or Full plan."],
    ],
  },
  about: {
    title: "About Amden",
    intro: "We build practical software for the businesses that keep the world running — trades, service firms and small teams.",
    hero: SHOTS.team,
    sections: [
      ["Practical over flashy", "Every screen follows a clear, consistent frame so you always know what to do next."],
      ["Secure by design", "Strict tenant isolation, role-based access, audit trails and encrypted transport are built in from day one."],
      ["Built to grow with you", "Start free as a sole trader and scale up to a full team without switching tools."],
    ],
  },
};

export default function InfoPage({ page }) {
  const c = CONTENT[page] || CONTENT.features;
  return (
    <div>
      <section className="bg-navy grain-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">{c.title}</h1>
          <p className="mt-5 text-lg text-slate-300 max-w-2xl mx-auto">{c.intro}</p>
        </div>
      </section>

      {c.hero && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-10">
          <BrowserFrame src={c.hero} alt={c.title} url={c.heroUrl} className="animate-fade-up" />
        </div>
      )}

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid gap-6 sm:grid-cols-2">
          {c.sections.map(([t, d], i) => (
            <div key={t} className="amden-card p-6 animate-fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="h-8 w-8 rounded-lg bg-blue-600 text-white grid place-items-center font-mono font-semibold text-sm">{i + 1}</div>
              <h3 className="mt-4 font-semibold text-navy text-lg">{t}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        {c.gallery && (
          <div className="mt-16">
            <h2 className="text-center text-2xl font-bold text-navy">A peek inside the platform</h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              {c.gallery.map(([src, label]) => (
                <figure key={label} className="amden-card overflow-hidden animate-fade-up">
                  <img src={src} alt={label} loading="lazy" className="w-full object-cover" />
                  <figcaption className="px-4 py-3 text-sm font-medium text-slate-600 border-t border-slate-100">{label}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-14">
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg transition-colors">Start free <ArrowRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
